import { useEffect, useState } from "react";

import {
  ArrowButton,
  TextField,
  Checkbox,
  Select,
  TableMetadata,
  TableStateWithFilters,
  useTable,
  Label,
  ColumnSortEnum,
  Typography,
  Button,
} from "@king-ict/eupisi-ui/components";
import { PagedResult, CodeBook } from "@king-ict/eupisi-ui/models";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models/Export/ExportTypeEnum";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { searchDepartment } from "../../api/Department/DepartmentApi";
import { getInstitutionById } from "../../api/Institution/InstitutionApi";
import {
  searchRequestProgramDepartment,
  getQuestionaireZipExport,
  getRequestDepartmentProgramExport,
} from "../../api/Request/RequestApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  InstitutionSelect,
  ListTable,
  SearchCard,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { useInput } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import { Role } from "../../models";
import {
  RequestDepartmentProgramElectiveSubjectForeignLanguage,
  RequestDepartmentProgramSearch,
  RequestDepartmentProgramSort,
  RequestStatus,
  RequestType,
  SynchronizationStatus,
} from "../../models/Request";
import { ClaimType } from "../../models/User";
import {
  useGetSchoolYearCurrentQuery,
  useGetSchoolYearListQuery,
  useGetInstitutionListForUserQuery,
} from "../../queries/codeBookQuery";
import { FormatUtils, MsgUtils, TableUtils } from "../../utils";

export function RequestDepartmentProgramList() {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const { data: schoolYears } = useGetSchoolYearListQuery();

  //STORAGE KEY
  const storageKey = "RequestDepartmentProgramSearchTableState";
  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const createInitialTableState = () => {
    return TableUtils.CreateInitialTableState<RequestDepartmentProgramSearch>(
      storageKey,
      TableUtils.tableDefaultState(
        RequestDepartmentProgramSort.LastName,
        ColumnSortEnum.ASC,
        10
      )
    );
  };
  const [initialTableState] = useState<
    TableStateWithFilters<RequestDepartmentProgramSearch>
  >(() => createInitialTableState());
  //INIT SEARCH FILTER
  const createInitialRequestDepartmentProgramSearch =
    (): RequestDepartmentProgramSearch => {
      return {
        schoolYearId:
          initialTableState.schoolYearId ??
          (schoolYears && schoolYears.length > 0 ? schoolYears[0].id : 0),
        enrollmentInstitutionId: initialTableState.enrollmentInstitutionId
          ? initialTableState.enrollmentInstitutionId
          : defaultInstitutionIds,
        requestType: initialTableState.requestType,
      };
    };
  const defaultInstitutionIds: number =
    // location only represents an Institution if ClaimType = Institution
    auth.currentRole?.claimType === ClaimType.Institution &&
    (auth.currentRole?.locations?.length ?? 0) > 0 &&
    auth.currentRole?.locations &&
    auth.currentRole?.locations[0]
      ? auth!.currentRole!.locations![0]
      : (0 as number);
  const defaultTableData =
    TableUtils.tableDefaultData<RequestDepartmentProgramElectiveSubjectForeignLanguage>();
  const [tableData, setTableData] =
    useState<
      PagedResult<RequestDepartmentProgramElectiveSubjectForeignLanguage>
    >(defaultTableData);
  //SET SEARCH FILTERS
  const [dataSearch, setDataSearch] = useState<RequestDepartmentProgramSearch>(
    createInitialRequestDepartmentProgramSearch()
  );
  const [tableSearch, setTableSearch] =
    useState<RequestDepartmentProgramSearch>(
      createInitialRequestDepartmentProgramSearch()
    );
  const [filterChanged, setFilterChanged] = useState<boolean>(false);
  const [clearFilter, setClearFilter] = useState<boolean>(true);
  //TABLE METADATA
  const tableMetadata: TableMetadata<RequestDepartmentProgramElectiveSubjectForeignLanguage> =
    {
      rowKey: "id",
      columns: [
        {
          columnKey: "lastName",
          header:
            t("common.schoolCandidateModel.lastName") +
            "/" +
            t("common.schoolCandidateModel.firstName"),
          accessor: (
            item: RequestDepartmentProgramElectiveSubjectForeignLanguage
          ) => {
            return (
              <div>
                <div className={"text-nowrap"}>
                  {item.lastName},&nbsp;{item.firstName}
                </div>
                <div className={"text-nowrap"}>
                  {t("common.schoolCandidateModel.oib")}:&nbsp;{item.childOib}
                </div>
              </div>
            );
          },
          sortBy: RequestDepartmentProgramSort.LastName,
        },
        {
          columnKey: "dateOfBirth",
          header: t("common.schoolCandidateModel.dateOfBirth"),
          accessor: (
            item: RequestDepartmentProgramElectiveSubjectForeignLanguage
          ) => {
            return (
              <div className={"text-nowrap"}>
                {item.dateOfBirth ? hrDate(item.dateOfBirth) : ""}
              </div>
            );
          },
          sortBy: RequestDepartmentProgramSort.Birthday,
        },
        /*{
                columnKey: "electiveSubjects",
                header: t("requestDepartmentProgram.electiveSubjects"),
                accessor: (item: RequestDepartmentProgramElectiveSubjectForeignLanguage) => {
                    return (
                        <>
                            <div className={"text-nowrap"}>
                                {item.electiveSubjects?.join(',')}
                            </div>
                        </>
                    )
                },
            },
            {
                columnKey: "foreignLanguages",
                header: t("requestDepartmentProgram.foreignLanguages"),
                accessor: (item: RequestDepartmentProgramElectiveSubjectForeignLanguage) => {
                    return (
                        <>
                            <div className={"text-nowrap"}>
                                {item.mandatoryForeignLanguages?.join(',')}
                            </div>
                        </>
                    )
                },
            },*/
        {
          columnKey: "enrollmentProgramName",
          header: t("requestDepartmentProgram.program"),
          accessor: (
            item: RequestDepartmentProgramElectiveSubjectForeignLanguage
          ) => {
            return (
              <>
                <div className={"text-nowrap"}>
                  {item.enrollmentProgramName}
                </div>
              </>
            );
          },
        },
        {
          columnKey: "enrollmentDepartmentName",
          header: t("requestDepartmentProgram.department"),
          accessor: (
            item: RequestDepartmentProgramElectiveSubjectForeignLanguage
          ) => {
            return (
              <>
                <div className={"text-nowrap"}>
                  {item.enrollmentDepartmentName}
                </div>
              </>
            );
          },
        },
        /*{
                columnKey: "type",
                header: t("common.requestModel.requestType"),
                accessor: (item: RequestDepartmentProgramElectiveSubjectForeignLanguage) => {
                    return (
                        <>
                            <Label color={FormatUtils.getRequestTypeColor(item.requestType)} className="text-center" outline={false} fullWidth={false}>
                                {types.find(v => v.id === item.requestType)?.name}
                            </Label>
                        </>
                    )
                },
                sortBy: RequestDepartmentProgramSort.Type,
            },*/
        {
          columnKey: "synchronizationStatus",
          header: t("requestDepartmentProgram.synchronizationStatus"),
          accessor: (
            item: RequestDepartmentProgramElectiveSubjectForeignLanguage
          ) => {
            return (
              <>
                <div className={"text-nowrap"}>
                  {
                    synchronizationStatuses?.find(
                      (v) => v.id === item.synchronizationStatus
                    )?.name
                  }
                </div>
              </>
            );
          },
        },
      ],
      rowToolTip: () => undefined,
    };
  //SET TABLE
  const [tableProps, tableState, setFilters] =
    useTable<RequestDepartmentProgramSearch>({
      metadata: tableMetadata,
      data: tableData!.results,
      pagination: true,
      paginationPage: initialTableState.page,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: tableData!.rowCount,
      defaultSort: {
        order: initialTableState.order,
        sortBy: initialTableState.sortBy,
      },
    });
  //FILTER DATA
  //const [filteredInstitutions, setFilteredInstitutions] = useState<CodeBook[]>([] as CodeBook[]);
  const [synchronizationStatuses] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(SynchronizationStatus, "SynchronizationStatus")
  );
  const [types] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(RequestType, "requestType")
  );
  const { data: institutions } = useGetInstitutionListForUserQuery();
  const [departments, setDepartments] = useState<CodeBook[] | undefined>();
  const [programs, setPrograms] = useState<CodeBook[] | undefined>();

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearch]);

  useEffect(() => {
    if (
      schoolYears &&
      schoolYears.length > 0 &&
      dataSearch.schoolYearId === 0
    ) {
      var schoolYear = schoolYears.find((v) => v.isCurrent);
      if (schoolYear) {
        const newSearch: RequestDepartmentProgramSearch = {
          ...dataSearch,
          //institutionIds: institutionList && institutionList.length > 0 ? institutionList.map(val => val.id) : [],
          schoolYearId: schoolYear.id,
        };
        setDataSearch(newSearch);
      }
    }
  }, [schoolYears]);

  useEffect(() => {
    if (
      institutions &&
      institutions.length > 0 &&
      dataSearch.enrollmentInstitutionId === 0
    ) {
      var institution = institutions[0];
      const newSearch: RequestDepartmentProgramSearch = {
        ...dataSearch,
        enrollmentInstitutionId: institution.id,
      };
      setDataSearch(newSearch);
    }
  }, [institutions]);

  useEffect(() => {
    (async () => {
      if (
        dataSearch.schoolYearId > 0 &&
        dataSearch.enrollmentInstitutionId > 0
      ) {
        var departmentsPromise = searchDepartment({
          schoolYearId: dataSearch.schoolYearId,
          institutionId: dataSearch.enrollmentInstitutionId,
        });
        var institutionPromise = getInstitutionById(
          dataSearch.enrollmentInstitutionId,
          dataSearch.schoolYearId
        );

        var departmentList = await departmentsPromise;
        if (departmentList.results) {
          var d = departmentList.results.map(
            (i) => ({ id: i.id, name: i.label } as CodeBook)
          );
          setDepartments(d);
          if (d.length > 0) {
            const newSearch: RequestDepartmentProgramSearch = {
              ...dataSearch,
              departmentId: d[0].id,
            };
            setDataSearch(newSearch);
          }
        }
        var institution = await institutionPromise;
        if (institution?.programs) {
          var p = institution.programs.map(
            (i) => ({ id: i.id, name: i.name } as CodeBook)
          );
          setPrograms(p);
        }
      } else {
        setDepartments(undefined);
        setPrograms(undefined);
      }
    })();
  }, [dataSearch.schoolYearId, dataSearch.enrollmentInstitutionId]);

  useEffect(() => {
    (async () => {
      var request = TableUtils.GetSearchRequest(tableSearch, tableState);
      if (
        request.schoolYearId > 0 &&
        request.enrollmentInstitutionId > 0 &&
        request.departmentId
      ) {
        var itemList = await searchRequestProgramDepartment(request);
        if (itemList !== null && itemList.results.length > 0) {
          setTableData(itemList);
        } else {
          setTableData(defaultTableData);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleInstitutionChange = (id: number) => {
    //const institutionList = e.target.value as CodeBook[];
    const newSearch: RequestDepartmentProgramSearch = {
      ...dataSearch,
      //institutionIds: institutionList && institutionList.length > 0 ? institutionList.map(val => val.id) : [],
      enrollmentInstitutionId: !isNaN(id) ? id : 0,
    };
    setDataSearch(newSearch);
  };

  const handleSearchClicked = (e: any) => {
    setTableSearch(dataSearch);
    setFilterChanged(false);
    doSearch();
  };
  const handleClearSearchClicked = () => {
    setClearFilter(true);
    const newRequestDataSearch: RequestDepartmentProgramSearch = {
      schoolYearId: schoolYears?.find((v) => v.isCurrent)?.id ?? 0,
      enrollmentInstitutionId: defaultInstitutionIds,
    };
    setDataSearch(newRequestDataSearch);
    setTableSearch(newRequestDataSearch);
    doSearch();
  };
  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    var search = TableUtils.GetSearchRequest(tableSearch, tableState);
    await getRequestDepartmentProgramExport(search, exportType);
  };
  const handleZipDownloadClicked = async () => {
    var search = TableUtils.GetSearchRequest(tableSearch, tableState);
    await getQuestionaireZipExport(search);
  };
  const doSearch = () => {
    setFilters({ ...tableSearch });
  };

  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("requestDepartmentProgram.pageName")}
        showAdd={"hide"}
      />
      <SearchCard
        showSearch={dataSearch.departmentId != undefined ? "show" : "disable"}
        onClickSearch={handleSearchClicked}
        onClickClearSearch={handleClearSearchClicked}
        isFilterChanged={filterChanged}
        hideDivider={true}
      >
        <Row>
          <Col md={12}>
            <Select
              label={t("common.schoolCandidateModel.schoolYearId")}
              name={nameof<typeof dataSearch>("schoolYearId")}
              value={dataSearch.schoolYearId}
              required={true}
              onChange={handleChange(setDataSearch)}
              selectData={codeBookToSelectData(schoolYears)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof dataSearch>("enrollmentInstitutionId")}
              value={dataSearch.enrollmentInstitutionId}
              countyIds={
                auth.currentRole?.claimType === ClaimType.County
                  ? auth.currentRole.locations
                  : undefined
              }
              ids={
                auth.currentRole?.claimType === ClaimType.Institution
                  ? auth.currentRole.locations
                  : undefined
              }
              onlyByEnrollmentArea={false}
              excludeHandicapped={false}
              excludePrivate={false}
              excludeSatellite={false}
              onChange={(e) =>
                handleInstitutionChange(Number.parseInt(e.target.value))
              }
              showUser={true}
              allowEmpty={false}
            />
          </Col>
          {/*<Col md={12}>
                        <Select
                            label={t("common.schoolCandidateModel.institutionList")}
                            name={nameof<typeof dataSearch>("enrollmentInstitutionId")}
                            value={dataSearch.enrollmentInstitutionId > 0 ? dataSearch.enrollmentInstitutionId : (institutions != undefined && institutions.length > 0 ? institutions[0].id : 0)}
                            required={true}
                            onChange={handleChange(setDataSearch)}
                            selectData={codeBookToSelectData(institutions?.map(v => ({id: v.id, name: v.name} as CodeBook)))}
                        />
                    </Col>*/}
        </Row>
        <Row>
          <Col md={6}>
            <Select
              label={t("requestDepartmentProgram.department")}
              name={nameof<typeof dataSearch>("departmentId")}
              value={dataSearch.departmentId}
              required={true}
              onChange={handleChange(setDataSearch)}
              selectData={codeBookToSelectData(departments)}
            />
          </Col>
          <Col md={6}>
            <Select
              label={t("requestDepartmentProgram.program")}
              name={nameof<typeof dataSearch>("enrollmentProgramId")}
              value={dataSearch.enrollmentProgramId}
              required={false}
              onChange={handleChange(setDataSearch)}
              selectData={codeBookToSelectData(programs)}
              emptyOption={{ value: undefined, text: undefined }}
            />
          </Col>
        </Row>
      </SearchCard>
      <ListTable
        tableProps={tableProps}
        onClickDownload={(exportType: ExportTypeEnum) =>
          handleDownloadClicked(exportType)
        }
        showDownload={tableData?.results && tableData.results.length > 0}
      >
        <Button onClick={handleZipDownloadClicked} size="sm">
          {t("requestDepartmentProgram.downloadZip")}
        </Button>
      </ListTable>
    </ContentArea>
  );
}
